import React, { useState, useEffect } from 'react';
import { getAuth } from 'firebase/auth';
import { getDatabase, ref, get } from 'firebase/database';
import { getStorage, ref as storageRef, uploadBytesResumable, getDownloadURL } from 'firebase/storage';
import { useNavigate } from 'react-router-dom';
import { widgetTemplates } from './widgetTemplates';
import { titleFonts, textFonts } from '../utils/fontsHelper';
import { useFont } from '../Design/FontContext';
import { authenticatedFetch } from '../utils/cloudFunctions';
import './AIPageCreator.css';

// Saved Palette Selector Component (uses persistent Firebase palettes)
const SavedPaletteSelector = ({ onPaletteSelect, currentSpaceColors, spaceId }) => {
    const [savedPalettes, setSavedPalettes] = useState([]);
    const [selectedPalette, setSelectedPalette] = useState('current');

    useEffect(() => {
        const loadSavedPalettes = async () => {
            const auth = getAuth();
            const currentUser = auth.currentUser;
            if (!currentUser) return;
            
            try {
                const db = getDatabase();
                // Use space-specific path if spaceId is provided, otherwise fall back to global
                const palettesPath = spaceId 
                    ? `/users/${currentUser.uid}/spaces/${spaceId}/savedColorPalettes`
                    : `/users/${currentUser.uid}/savedColorPalettes`;
                const palettesRef = ref(db, palettesPath);
                const snapshot = await get(palettesRef);
                
                if (snapshot.exists()) {
                    const palettesData = snapshot.val();
                    const palettesArray = Object.entries(palettesData).map(([key, value]) => ({
                        id: key,
                        ...value
                    }));
                    setSavedPalettes(palettesArray);
                    console.log(`✅ Loaded ${palettesArray.length} saved palettes for ${spaceId ? `space ${spaceId}` : 'global'}`);
                } else {
                    setSavedPalettes([]);
                }
            } catch (error) {
                console.error('Error loading saved palettes:', error);
            }
        };
        
        loadSavedPalettes();
    }, [spaceId]);

    const handlePaletteSelect = (palette) => {
        if (palette.id === 'current') {
            setSelectedPalette('current');
            // Keep current space colors
        } else {
            setSelectedPalette(palette.id);
            onPaletteSelect(palette);
        }
    };

    return (
        <div className="saved-palette-selector">
            {/* Current Space Colors */}
            <div 
                className={`palette-option ${selectedPalette === 'current' ? 'active' : ''}`}
                onClick={() => handlePaletteSelect({ id: 'current' })}
            >
                <div className="palette-preview">
                    {currentSpaceColors && Object.values(currentSpaceColors).map((color, index) => (
                        <div 
                            key={index}
                            className="palette-color-mini"
                            style={{ backgroundColor: color }}
                        ></div>
                    ))}
                </div>
                <div className="palette-info">
                    <span className="palette-name">Current Space Colors</span>
                    <span className="palette-description">Use your space's default colors</span>
                </div>
            </div>

            {/* Saved Palettes */}
            {savedPalettes.map((palette) => (
                <div 
                    key={palette.id}
                    className={`palette-option ${selectedPalette === palette.id ? 'active' : ''}`}
                    onClick={() => handlePaletteSelect(palette)}
                >
                    <div className="palette-preview">
                        {Object.values(palette.colors).map((color, index) => (
                            <div 
                                key={index}
                                className="palette-color-mini"
                                style={{ backgroundColor: color }}
                            ></div>
                        ))}
                    </div>
                    <div className="palette-info">
                        <span className="palette-name">{palette.name}</span>
                        <span className="palette-description">{palette.style} style</span>
                    </div>
                </div>
            ))}
        </div>
    );
};

/**
 * AI Page Creator Modal - Clean & Simple Version
 * Uses space settings and minimal user input for AI page generation
 */
const AIPageCreator = ({ onClose, spaceId, spaceName, username }) => {
    const [creating, setCreating] = useState(false);
    const [error, setError] = useState('');
    const [spaceSettings, setSpaceSettings] = useState(null);
    const [loading, setLoading] = useState(true);
    
    // FONT CONTEXT INTEGRATION
    const fontContext = useFont();
    
    // ASSET-UPLOAD STATES
    const [uploadedAssets, setUploadedAssets] = useState([]);
    const [uploading, setUploading] = useState(false);
    const [uploadProgress, setUploadProgress] = useState(0);
    
    // VEREINFACHTE AI INPUTS (Space-Daten werden automatisch geladen)
    const [aiInputs, setAiInputs] = useState({
        businessDescription: '' // ← Nur noch die Haupteingabe
    });
    
    // COLOR-ASSIGNMENT STATES
    const [colorAssignments, setColorAssignments] = useState({
        background: 'color5',      // Heller Hintergrund
        primaryText: 'color2',     // Dunkler Text
        secondaryText: 'color3',   // Mittlerer Text
        buttons: 'color1',         // Akzent für Buttons
        buttonText: 'color5',      // Button-Text-Farbe (meist Kontrast zu Button)
        accents: 'color1'          // Highlights & Akzente
    });
    
    // FLAG: Verhindert dass Space Settings User-Auswahl überschreiben
    const [userModifiedColorAssignments, setUserModifiedColorAssignments] = useState(false);
    
    // Reset color assignments to defaults when new palette is selected
    useEffect(() => {
        if (spaceSettings?.colors) {
            console.log('🎨 [COLOR-SYNC] Resetting color assignments for new palette', spaceSettings.colors);
            
            // Force state update with slight delay to ensure proper React rendering
            setTimeout(() => {
                setColorAssignments({
                    background: 'color5',      
                    primaryText: 'color2',     
                    secondaryText: 'color3',   
                    buttons: 'color1',         
                    buttonText: 'color5',      
                    accents: 'color1'          
                });
                console.log('🎨 [COLOR-SYNC] Color assignments reset completed');
            }, 50);
        }
    }, [JSON.stringify(spaceSettings?.colors)]); // Use JSON.stringify to detect object changes

    // INTELLIGENTE BUTTON-TEXT-FARB-LOGIK
    const getContrastColor = (buttonColorKey, colors) => {
        if (!colors || !buttonColorKey) return 'color5';
        
        const buttonColor = colors[buttonColorKey];
        if (!buttonColor) return 'color5';
        
        // Einfache Helligkeit-Berechnung
        const hex = buttonColor.replace('#', '');
        const r = parseInt(hex.substr(0, 2), 16);
        const g = parseInt(hex.substr(2, 2), 16);
        const b = parseInt(hex.substr(4, 2), 16);
        const brightness = (r * 299 + g * 587 + b * 114) / 1000;
        
        // Bei dunklen Button-Farben → helle Text-Farbe, bei hellen Button-Farben → dunkle Text-Farbe
        return brightness < 128 ? 'color5' : 'color2'; // color5 = hell, color2 = dunkel
    };

    // AUTO-ADJUST Button-Text-Farbe wenn Button-Farbe geändert wird
    const handleButtonColorChange = (newButtonColor) => {
        const colors = spaceSettings?.colors || spaceSettings?.designSettings?.colors;
        const suggestedTextColor = getContrastColor(newButtonColor, colors);
        
        setColorAssignments(prev => ({ 
            ...prev, 
            buttons: newButtonColor,
            buttonText: suggestedTextColor
        }));
        
        setUserModifiedColorAssignments(true); // ✅ User hat Änderung gemacht
        console.log(`🎨 [SMART-BUTTON] Button color changed to ${newButtonColor}, suggested text color: ${suggestedTextColor}`);
    };
    
    // PAGE-COUNT SLIDER
    const [pageCount, setPageCount] = useState(3);
    
    // LANGUAGE SELECTION STATE
    const [selectedLanguage, setSelectedLanguage] = useState('English');
    
    // FONT-SELECTION STATES
    const [selectedFonts, setSelectedFonts] = useState({
        globalBigFont: 'Voyage',   // Default
        globalSmallFont: 'Jost'    // Default
    });

    // PROGRESS ANIMATION STATE
    const [currentStep, setCurrentStep] = useState(1);

    const auth = getAuth();
    const navigate = useNavigate();
    const currentUser = auth.currentUser;
    const db = getDatabase();
    const storage = getStorage();

    // SUPPORTED LANGUAGES - English als Standard, dann Deutsch, dann 10 populäre Sprachen
    const supportedLanguages = [
        { code: 'en', name: 'English', flag: '🇺🇸' },
        { code: 'de', name: 'Deutsch', flag: '🇩🇪' },
        { code: 'es', name: 'Español', flag: '🇪🇸' },
        { code: 'fr', name: 'Français', flag: '🇫🇷' },
        { code: 'it', name: 'Italiano', flag: '🇮🇹' },
        { code: 'pt', name: 'Português', flag: '🇵🇹' },
        { code: 'nl', name: 'Nederlands', flag: '🇳🇱' },
        { code: 'sv', name: 'Svenska', flag: '🇸🇪' },
        { code: 'da', name: 'Dansk', flag: '🇩🇰' },
        { code: 'no', name: 'Norsk', flag: '🇳🇴' },
        { code: 'fi', name: 'Suomi', flag: '🇫🇮' },
        { code: 'ru', name: 'Русский', flag: '🇷🇺' }
    ];

    // Combine system fonts with custom fonts
    const allTitleFonts = [
        ...titleFonts,
        ...(fontContext.customFonts?.map(f => f.name) || [])
    ];
    
    const allTextFonts = [
        ...textFonts,
        ...(fontContext.customFonts?.map(f => f.name) || [])
    ];

    const isCustomFont = (fontName) => {
        return fontContext.customFonts?.some(f => f.name === fontName);
    };

    // SPACE-SETTINGS AUTOMATISCH LADEN
    useEffect(() => {
        const loadSpaceSettings = async () => {
            if (!currentUser || !spaceId) return;
            
            console.log(`📋 [SPACE-LOADING] Loading space settings for: ${spaceId}`);
            setLoading(true);
            
            try {
                const spaceRef = ref(db, `/users/${currentUser.uid}/spaces/${spaceId}`);
                const snapshot = await get(spaceRef);
                const spaceData = snapshot.val();
                
                if (spaceData) {
                    setSpaceSettings(prevSettings => {
                        // Wenn bereits Settings vorhanden sind und Farben manuell geändert wurden, 
                        // behalte die manuellen Farben bei
                        if (prevSettings?.colors) {
                            console.log('🎨 [SPACE-LOADING] Preserving manually selected colors');
                            return {
                                ...spaceData,
                                colors: prevSettings.colors // Behalte die ausgewählte Palette
                            };
                        } else {
                            console.log('🎨 [SPACE-LOADING] Loading initial space settings');
                            return spaceData;
                        }
                    });
                    
                    // AUTO-FILL BUSINESS DESCRIPTION aus Space-Settings
                    // Business-Info kann in designSettings oder direkt im spaceData sein
                    const contextInfo = spaceData.contextInfo || spaceData.designSettings?.contextInfo || '';
                    
                    if (contextInfo && !aiInputs.businessDescription) {
                        setAiInputs(prev => ({
                            ...prev,
                            businessDescription: contextInfo
                        }));
                        console.log(`🔄 [AUTO-FILL] Business description loaded from space`);
                    }
                    
                    // AUTO-FILL FONTS aus Space-Settings
                    const spaceFonts = spaceData.designSettings;
                    if (spaceFonts) {
                        setSelectedFonts({
                            globalBigFont: spaceFonts.globalBigFont || 'Voyage',
                            globalSmallFont: spaceFonts.globalSmallFont || 'Jost'
                        });
                        console.log(`🔤 [AUTO-FILL] Space fonts loaded:`, {
                            big: spaceFonts.globalBigFont || 'Voyage',
                            small: spaceFonts.globalSmallFont || 'Jost'
                        });
                    }

                    // 🎨 AUTO-FILL COLOR ASSIGNMENTS aus Space-Settings (NUR wenn User nichts geändert hat)
                    const spaceColorAssignments = spaceData.designSettings?.colorAssignments;
                    if (spaceColorAssignments && !userModifiedColorAssignments) {
                        // Nur übernehmen wenn User noch nichts manuell geändert hat
                        setColorAssignments(spaceColorAssignments);
                        console.log(`🎨 [AUTO-FILL] Space color assignments loaded (user has not modified):`, spaceColorAssignments);
                    } else if (spaceColorAssignments && userModifiedColorAssignments) {
                        console.log(`🎯 [AUTO-FILL] User has modified color assignments, NOT overriding with space settings`);
                        console.log(`🎯 [AUTO-FILL] Current user selection:`, colorAssignments);
                        console.log(`🎯 [AUTO-FILL] Space settings (ignored):`, spaceColorAssignments);
                    } else {
                        console.log(`⚠️ [AUTO-FILL] No color assignments found in space settings, using defaults`);
                    }
                    
                    console.log(`✅ [SPACE-LOADING] Space settings loaded:`, {
                        spaceName: spaceData.spaceName,
                        ownerName: spaceData.ownerName,
                        contextInfo: spaceData.contextInfo,
                        hasColors: !!spaceData.colors
                    });
                } else {
                    console.log(`⚠️ [SPACE-LOADING] No space data found`);
                }
            } catch (error) {
                console.error(`❌ [SPACE-LOADING] Error loading space settings:`, error);
                setError('Could not load space settings. Please try again.');
            } finally {
                setLoading(false);
            }
        };

        loadSpaceSettings();
    }, [currentUser, spaceId, spaceName, username, db]);

    // PROGRESS STEP ANIMATION
    useEffect(() => {
        if (!creating) {
            setCurrentStep(1);
            return;
        }

        const stepTimings = [
            { step: 1, delay: 500 },   // Analyzing content
            { step: 2, delay: 2500 },  // Selecting widgets  
            { step: 3, delay: 6000 },  // Generating content
            { step: 4, delay: 9000 }   // Applying design
        ];

        const timeouts = stepTimings.map(({ step, delay }) => 
            setTimeout(() => setCurrentStep(step), delay)
        );

        return () => timeouts.forEach(clearTimeout);
    }, [creating]);

    // ASSET-UPLOAD FUNKTIONEN (vereinfacht - erst upload, dann kategorisieren)
    const handleAssetUpload = async (files) => {
        if (!files || files.length === 0 || !currentUser) return;

        setUploading(true);
        setUploadProgress(0);

        try {
            const totalFiles = files.length;
            const fileProgress = {}; // Track individual file progress
            
            console.log(`📤 Starting upload of ${totalFiles} files`);

            const uploadPromises = Array.from(files).map(async (file, index) => {
                // Validate file type
                const validTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/webp', 'image/svg+xml'];
                if (!validTypes.includes(file.type)) {
                    console.warn(`Skipping invalid file type: ${file.type}`);
                    return null;
                }

                // Validate file size (max 5MB)
                if (file.size > 5 * 1024 * 1024) {
                    console.warn(`Skipping file too large: ${file.name}`);
                    return null;
                }

                // Generate unique filename
                const timestamp = Date.now();
                const randomId = Math.random().toString(36).substr(2, 9);
                const uniqueFileName = `ai-assets/${currentUser.uid}/${spaceId}/${timestamp}_${randomId}_${file.name}`;
                
                // Initialize progress for this file
                fileProgress[index] = 0;

                // Upload to Firebase Storage
                const fileRef = storageRef(storage, uniqueFileName);
                const uploadTask = uploadBytesResumable(fileRef, file);

                return new Promise((resolve, reject) => {
                    uploadTask.on('state_changed',
                        (snapshot) => {
                            // Update this file's progress
                            fileProgress[index] = (snapshot.bytesTransferred / snapshot.totalBytes) * 100;
                            
                            // Calculate total progress from all files
                            const totalProgress = Object.values(fileProgress).reduce((sum, progress) => sum + progress, 0) / totalFiles;
                            setUploadProgress(Math.min(totalProgress, 100));
                        },
                        (error) => {
                            console.error('Upload error:', error);
                            fileProgress[index] = 0;
                            reject(error);
                        },
                        async () => {
                            try {
                                const downloadURL = await getDownloadURL(uploadTask.snapshot.ref);
                                fileProgress[index] = 100;
                                
                                // Update total progress
                                const totalProgress = Object.values(fileProgress).reduce((sum, progress) => sum + progress, 0) / totalFiles;
                                setUploadProgress(Math.min(totalProgress, 100));
                                
                                resolve({
                                    id: `${timestamp}_${randomId}`,
                                    name: file.name,
                                    url: downloadURL,
                                    type: 'uncategorized', // Erst uncategorized, dann zuweisen
                                    uploadedAt: new Date().toISOString(),
                                    size: file.size
                                });
                            } catch (error) {
                                fileProgress[index] = 0;
                                reject(error);
                            }
                        }
                    );
                });
            });

            const uploadedFiles = await Promise.all(uploadPromises);
            const validUploads = uploadedFiles.filter(file => file !== null);

            // Add to uploaded assets
            setUploadedAssets(prev => [...prev, ...validUploads]);
            console.log(`✅ Uploaded ${validUploads.length} assets`);

        } catch (error) {
            console.error('Asset upload error:', error);
            setError(`Failed to upload assets: ${error.message}`);
        } finally {
            setUploading(false);
            setUploadProgress(0);
        }
    };

    // ASSET-KATEGORISIERUNG
    const updateAssetType = (assetId, newType) => {
        setUploadedAssets(prev => 
            prev.map(asset => 
                asset.id === assetId 
                    ? { ...asset, type: newType }
                    : asset
            )
        );
        console.log(`🏷️ Asset ${assetId} categorized as: ${newType}`);
    };

    const removeAsset = (assetId) => {
        setUploadedAssets(prev => prev.filter(asset => asset.id !== assetId));
    };

    const getAssetsByType = (type) => {
        return uploadedAssets.filter(asset => asset.type === type);
    };

    // AI PAGE GENERATION FUNCTION (nutzt Space-Settings)
    const handleGeneratePage = async () => {
        console.log(`🤖 [AI-PAGE] === STARTING AI PAGE GENERATION ===`);
        
        setCreating(true);
        setError('');
        
        try {
            const user = auth.currentUser;
            if (!user) {
                throw new Error('User not authenticated');
            }
            
            // SPACE-DATEN IN AI-FORMAT KONVERTIEREN
            const selectedLanguageCode = supportedLanguages.find(lang => lang.name === selectedLanguage)?.code || 'en';
            
            const enrichedInputs = {
                businessInfo: {
                    businessName: spaceSettings?.spaceName || spaceName || 'My Business',
                    businessDescription: aiInputs.businessDescription,
                    targetAudience: extractTargetAudience((spaceSettings?.contextInfo || spaceSettings?.designSettings?.contextInfo) || aiInputs.businessDescription),
                    ownerName: spaceSettings?.ownerName || spaceSettings?.designSettings?.ownerName || username,
                    contextInfo: spaceSettings?.contextInfo || spaceSettings?.designSettings?.contextInfo || ''
                },
                contentLanguage: {
                    name: selectedLanguage,
                    code: selectedLanguageCode
                },
                contentNeeds: {
                    hasTestimonials: true,
                    needsContactInfo: true,
                    includeAboutSection: true,
                    needsCTA: true,
                    socialProof: true
                },
                customerAssets: {
                    logo: getAssetsByType('logo')[0]?.url || null,
                    heroImage: getAssetsByType('hero')[0]?.url || null,
                    profileImage: getAssetsByType('profile')[0]?.url || null,
                    brollImages: getAssetsByType('broll').map(asset => asset.url),
                    icons: getAssetsByType('icon').map(asset => asset.url),
                    allAssets: uploadedAssets, // Alle Assets für AI-Auswahl
                    categorizedAssets: {
                        hero: getAssetsByType('hero'),
                        logo: getAssetsByType('logo'),
                        profile: getAssetsByType('profile'),
                        broll: getAssetsByType('broll'),
                        icon: getAssetsByType('icon'),
                        uncategorized: getAssetsByType('uncategorized')
                    },
                    brandColors: spaceSettings?.colors ? Object.values(spaceSettings.colors) : 
                                (spaceSettings?.designSettings?.colors ? Object.values(spaceSettings.designSettings.colors) : []),
                    // WICHTIG: Aktuelle Farben direkt aus dem State (nach Palette-Auswahl)
                    currentColors: spaceSettings?.colors || spaceSettings?.designSettings?.colors
                },
                colorAssignments: colorAssignments, // Color-Zuweisungen für AI
                pageCount: pageCount, // Anzahl zu generierender Pages
                spaceSettings: spaceSettings // ← Space-Settings mit übergeben
            };
            
            console.log(`📋 [AI-PAGE] Enriched inputs:`, enrichedInputs);
            console.log(`🎨 [AI-PAGE] Color data check:`, {
                spaceSettingsColors: spaceSettings?.colors,
                designSettingsColors: spaceSettings?.designSettings?.colors,
                colorAssignments: colorAssignments,
                brandColors: enrichedInputs.customerAssets.brandColors,
                userModifiedFlag: userModifiedColorAssignments
            });
            
            console.log(`🎯 [FRONTEND-SENDING] What we're sending to Cloud Function:`, {
                separateColorAssignments: colorAssignments,
                enrichedInputsColorAssignments: enrichedInputs.colorAssignments,
                areSame: JSON.stringify(colorAssignments) === JSON.stringify(enrichedInputs.colorAssignments)
            });
            console.log(`🔤 [AI-PAGE] Font data check:`, {
                selectedFonts: selectedFonts,
                customFonts: fontContext.customFonts,
                customFontCount: fontContext.customFonts?.length || 0
            });
            console.log(`🌐 [AI-PAGE] Language data check:`, {
                selectedLanguage: selectedLanguage,
                selectedLanguageCode: selectedLanguageCode,
                supportedLanguagesCount: supportedLanguages.length
            });
            console.log(`🌐 [AI-PAGE] Calling cloud function...`);
            
            // Dynamische URL basierend auf der Firebase-Konfiguration
            const db = getDatabase();
            const isDevEnvironment = db.app.options.databaseURL.includes('bonusclub-dev');
            const functionUrl = isDevEnvironment 
                ? 'https://us-central1-bonusclub-dev.cloudfunctions.net/createAiPage'
                : 'https://us-central1-bonusclub-ab5e2.cloudfunctions.net/createAiPage';
            
            console.log(`📡 [AI-PAGE] Function URL (${isDevEnvironment ? 'DEV' : 'PROD'}):`, functionUrl);
            console.log(`📡 [AI-PAGE] Database URL:`, db.app.options.databaseURL);
            
            const response = await authenticatedFetch(`${functionUrl}?uid=${user.uid}`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    inputs: enrichedInputs,
                    spaceId: spaceId,
                    widgetTemplates: widgetTemplates, // ← Widget-Templates vom Frontend senden
                    colorAssignments: colorAssignments, // Color-Zuweisungen für AI
                    pageCount: pageCount, // Anzahl zu generierender Pages
                    selectedFonts: selectedFonts, // Font-Auswahl für AI
                    customFonts: fontContext.customFonts || [], // ← Custom Font Metadaten
                    contentLanguage: { // ← NEUE SPRACH-PARAMETER
                        name: selectedLanguage,
                        code: selectedLanguageCode
                    },
                    // DIREKTE FARB-ÜBERTRAGUNG: Aktuelle Palette-Farben aus dem State
                    currentPaletteColors: spaceSettings?.colors || spaceSettings?.designSettings?.colors
                })
            });
            
            console.log(`📥 [AI-PAGE] Response status:`, response.status);
            
            if (!response.ok) {
                const errorText = await response.text();
                console.error(`❌ [AI-PAGE] HTTP Error:`, response.status, errorText);
                throw new Error(`HTTP ${response.status}: ${errorText}`);
            }
            
            const result = await response.json();
            console.log(`📊 [AI-PAGE] Result:`, result);
            
            if (result.success) {
                console.log(`🎉 [AI-PAGE] Page generated successfully:`, result.pageId);
                console.log(`📈 [AI-PAGE] Stats:`, result.stats);
                console.log(`🔗 [AI-PAGE] Page URL:`, result.pageUrl);
                
                // Redirect zu Page-Settings für Bearbeitung
                onClose();
                
                // Warte und prüfe, ob die Seite in Firebase verfügbar ist
                console.log(`⏳ [AI-PAGE] Waiting for Firebase sync before redirect...`);
                
                const waitForPageSync = async (pageId, maxAttempts = 10) => {
                    const db = getDatabase();
                    console.log(`🔍 [AI-PAGE] Using database for sync check:`, db.app.options.databaseURL);
                    
                    for (let attempt = 1; attempt <= maxAttempts; attempt++) {
                        console.log(`🔍 [AI-PAGE] Checking if page exists (attempt ${attempt}/${maxAttempts})...`);
                        console.log(`🔍 [AI-PAGE] Looking for page at path: pages/${pageId}`);
                        
                        try {
                            const pageRef = ref(db, `pages/${pageId}`);
                            const snapshot = await get(pageRef);
                            
                            if (snapshot.exists()) {
                                console.log(`✅ [AI-PAGE] Page found in Firebase! Redirecting...`);
                                navigate(`/settings/page/${pageId}`);
                                return;
                            } else {
                                console.log(`❌ [AI-PAGE] Page not found at pages/${pageId} (attempt ${attempt})`);
                            }
                        } catch (error) {
                            console.warn(`⚠️ [AI-PAGE] Error checking page (attempt ${attempt}):`, error);
                        }
                        
                        // Warte 1 Sekunde vor dem nächsten Versuch
                        await new Promise(resolve => setTimeout(resolve, 1000));
                    }
                    
                    // Wenn nach allen Versuchen die Seite nicht gefunden wurde, trotzdem weiterleiten
                    console.warn(`⚠️ [AI-PAGE] Page not found after ${maxAttempts} attempts. Redirecting anyway...`);
                    console.warn(`⚠️ [AI-PAGE] This might be a database mismatch issue (dev vs prod)`);
                    navigate(`/settings/page/${pageId}`);
                };
                
                waitForPageSync(result.pageId);
                
            } else {
                throw new Error(result.error || 'Page generation failed');
            }
            
        } catch (error) {
            console.error(`❌ [AI-PAGE] Generation failed:`, error);
            setError(`AI Page generation failed: ${error.message}`);
        } finally {
            setCreating(false);
        }
    };

    // Helper: Target Audience aus Context extrahieren
    const extractTargetAudience = (text) => {
        if (!text) return 'Potential customers';
        
        // Einfache Keyword-Extraktion
        const keywords = text.toLowerCase();
        if (keywords.includes('frauen') || keywords.includes('women')) return 'Frauen';
        if (keywords.includes('männer') || keywords.includes('men')) return 'Männer';
        if (keywords.includes('unternehmen') || keywords.includes('business')) return 'Unternehmen';
        if (keywords.includes('spiritual') || keywords.includes('meditation')) return 'Spirituell Suchende';
        
        return 'Zielgruppe'; // Fallback
    };

    return (
        <div 
            className="ai-page-creator-overlay"
        >
            <div className={`ai-page-creator ${creating ? 'generating' : ''}`}>
                {/* Close Button */}
                <button 
                    className="ai-page-creator-close-btn"
                    onClick={() => {
                        onClose();
                        document.body.style.overflow = 'auto';
                    }}
                    title="Close"
                    disabled={creating}
                >
                    <i className="fas fa-times"></i>
                </button>
                
                {/* AI Generation Overlay - Neues Brand Design */}
                {creating && (
                    <div className="ai-generation-overlay">
                        <div className="ai-generation-content">
                            {/* Brand Logo/Icon */}
                            <div className="brand-loading-logo">
                                <div className="logo-ring"></div>
                                <div className="logo-center">
                                    <i className="fas fa-magic"></i>
                                </div>
                            </div>
                            
                            {/* Elegant Title */}
                            <div className="loading-title-section">
                                <h3 className="loading-main-title gradient-animated-text">Creating your page</h3>
                                <div className="loading-subtitle">AI is working its magic</div>
                            </div>
                            
                            {/* Progress Visualization */}
                            <div className="progress-visualization">
                                <div className="progress-steps">
                                    <div className={`step-dot ${currentStep >= 1 ? 'active' : ''} ${currentStep > 1 ? 'completed' : ''}`} data-step="1">
                                        <i className={`fas ${currentStep > 1 ? 'fa-check' : 'fa-brain'}`}></i>
                                    </div>
                                    <div className={`step-line ${currentStep > 1 ? 'active' : ''}`}></div>
                                    <div className={`step-dot ${currentStep >= 2 ? 'active' : ''} ${currentStep > 2 ? 'completed' : ''}`} data-step="2">
                                        <i className={`fas ${currentStep > 2 ? 'fa-check' : 'fa-puzzle-piece'}`}></i>
                                    </div>
                                    <div className={`step-line ${currentStep > 2 ? 'active' : ''}`}></div>
                                    <div className={`step-dot ${currentStep >= 3 ? 'active' : ''} ${currentStep > 3 ? 'completed' : ''}`} data-step="3">
                                        <i className={`fas ${currentStep > 3 ? 'fa-check' : 'fa-pen-fancy'}`}></i>
                                    </div>
                                    <div className={`step-line ${currentStep > 3 ? 'active' : ''}`}></div>
                                    <div className={`step-dot ${currentStep >= 4 ? 'active' : ''}`} data-step="4">
                                        <i className="fas fa-palette"></i>
                                    </div>
                                </div>
                                
                                <div className="current-step-label">
                                    <div className={`step-text ${currentStep === 1 ? 'active' : ''}`} data-step="1">Analyzing content</div>
                                    <div className={`step-text ${currentStep === 2 ? 'active' : ''}`} data-step="2">Selecting widgets</div>
                                    <div className={`step-text ${currentStep === 3 ? 'active' : ''}`} data-step="3">Generating content</div>
                                    <div className={`step-text ${currentStep === 4 ? 'active' : ''}`} data-step="4">Applying design</div>
                                </div>
                            </div>
                            
                            {/* Estimated time */}
                            <div className="loading-eta">
                                <i className="fas fa-clock"></i>
                                <span>Usually takes 5-10 seconds</span>
                            </div>
                        </div>
                    </div>
                )}

                {/* Header */}
                <div className="ai-page-creator-header">
                    <div className="ai-page-creator-icon">
                  <img src="/img/abstract_1.svg" alt="" style={{ width: '22px'}}/>
                    </div>
                    <h2>AI Page Creator</h2>
                    <p>Let AI help you create the perfect page for your business. Describe what you want to achieve.</p>
                </div>

                {/* Content */}
                <div className="ai-page-creator-content">
                    {loading ? (
                        <div className="ai-loading-state">
                            <div className="ai-loading-spinner">
                                <i className="fas fa-spinner fa-spin"></i>
                            </div>
                            <p>Loading your space settings...</p>
                        </div>
                    ) : (
                        <>
                            {/* AI Description Input - Haupteingabe */}
                            <div className="ai-page-creator-section">
                                <h3><i className="fas fa-bullseye"></i> Describe Your Page</h3>
                                <p>Tell AI about your business and what kind of page you need. The more details, the better the result!</p>
                                
                                <div className="ai-description-container">
                                    <textarea
                                        value={aiInputs.businessDescription}
                                        onChange={(e) => setAiInputs(prev => ({ ...prev, businessDescription: e.target.value }))}
                                        placeholder="z.B. Ich bin ein spiritueller Coach und brauche eine Landing Page für mein neues Meditation-Programm. Meine Zielgruppe sind Frauen 25-45 die Stress abbauen und innere Ruhe finden wollen. Die Page soll vertrauensvoll und beruhigend wirken..."
                                        className="ai-description-textarea"
                                        rows={6}
                                    />
                                </div>
                                
                            </div>

                            {/* Language Selection */}
                            <div className="ai-page-creator-section">
                                <h3><i className="fas fa-globe"></i> Content Language</h3>
                                <p>Choose the language for your AI-generated content. All texts will be created in the selected language.</p>
                                
                                <div className="ai-language-selection">
                                    <div className="ai-language-pills">
                                        {supportedLanguages.map(language => (
                                            <div 
                                                key={language.code}
                                                className={`ai-language-pill ${selectedLanguage === language.name ? 'selected' : ''}`}
                                                onClick={() => {
                                                    setSelectedLanguage(language.name);
                                                    console.log(`🌐 [LANGUAGE] Selected language: ${language.name} (${language.code})`);
                                                }}
                                                title={`Generate content in ${language.name}`}
                                            >
                                                <span className="language-flag">{language.flag}</span>
                                                <span className="language-name">{language.name}</span>
                                                {selectedLanguage === language.name && (
                                                    <i className="fas fa-check language-check"></i>
                                                )}
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            </div>

                            {/* Asset-Upload-Sektion */}
                            <div className="ai-page-creator-section">
                                <h3><i className="fas fa-images"></i> Upload Your Assets</h3>
                                <p>Upload all your images first, then categorize them below. The AI will intelligently place them based on their categories.</p>
                                
                                {/* Single Upload Area */}
                                <div className="single-upload-area">
                                    <input
                                        type="file"
                                        id="bulk-upload"
                                        multiple
                                        accept="image/*"
                                        onChange={(e) => handleAssetUpload(e.target.files)}
                                        style={{ display: 'none' }}
                                    />
                                    <label htmlFor="bulk-upload" className="bulk-upload-button">
                                        <i className="fas fa-cloud-upload-alt"></i>
                                        <span>Upload Multiple Images</span>
                                        <small>Drag & drop or click to select images</small>
                                    </label>
                                </div>

                                {/* Upload Progress */}
                                {uploading && (
                                    <div className="upload-progress">
                                        <div className="progress-bar">
                                            <div 
                                                className="progress-fill" 
                                                style={{ width: `${uploadProgress}%` }}
                                            ></div>
                                        </div>
                                        <p>Uploading assets... {Math.round(uploadProgress)}%</p>
                                    </div>
                                )}

                                {/* Asset Categorization */}
                                {uploadedAssets.length > 0 && (
                                    <div className="asset-categorization">
                                        <h4><i className="fas fa-tags"></i> Categorize Your Assets</h4>
                                        <p>Assign each image to a category so the AI knows how to use them.</p>
                                        
                                        <div className="assets-grid">
                                            {uploadedAssets.map((asset, index) => (
                                                <div 
                                                    key={asset.id} 
                                                    className="asset-item"
                                                    style={{ animationDelay: `${index * 0.1}s` }}
                                                >
                                                    <div className="asset-preview">
                                                        <img src={asset.url} alt={asset.name} />
                                                        <button 
                                                            className="remove-asset-btn"
                                                            onClick={() => removeAsset(asset.id)}
                                                            title="Remove asset"
                                                        >
                                                            <i className="fas fa-times"></i>
                                                        </button>
                                                    </div>
                                                    
                                                    <div className="asset-details">
                                                        <span className="asset-name">{asset.name}</span>
                                                        <select 
                                                            value={asset.type}
                                                            onChange={(e) => updateAssetType(asset.id, e.target.value)}
                                                            className="asset-type-selector"
                                                        >
                                                            <option value="uncategorized">Select Category...</option>
                                                            <option value="hero">🖼️ Hero Image</option>
                                                            <option value="logo">🏆 Logo</option>
                                                            <option value="broll">📸 B-Roll</option>
                                                            <option value="icon">⭐ Icon</option>
                                                            <option value="profile">👤 Profile</option>
                                                        </select>
                                                    </div>
                                                </div>
                                            ))}
                                        </div>

                                    </div>
                                )}
                            </div>

                            {/* Color Assignment System */}
                            {console.log('🎨 [DEBUG] Space settings for colors:', { 
                                hasSpaceSettings: !!spaceSettings,
                                hasColors: !!(spaceSettings?.colors),
                                hasDesignColors: !!(spaceSettings?.designSettings?.colors),
                                spaceSettings: spaceSettings
                            })}
                            {/* Saved Palettes Selection */}
                            <div className="ai-page-creator-section">
                                <h3><i className="fas fa-bookmark"></i> Choose Color Palette</h3>
                                <p>Use your saved palettes or the current space colors for this page.</p>
                                
                                <div className="saved-palettes-selector">
                                    <SavedPaletteSelector 
                                        onPaletteSelect={(palette) => {
                                            console.log('🎨 [PALETTE-SELECT] Palette selected:', palette);
                                            // Update space settings with selected palette
                                            setSpaceSettings(prev => {
                                                const updated = {
                                                    ...prev,
                                                    colors: palette.colors
                                                };
                                                console.log('🎨 [PALETTE-SELECT] Updated spaceSettings:', updated);
                                                return updated;
                                            });
                                        }}
                                        currentSpaceColors={spaceSettings?.colors || spaceSettings?.designSettings?.colors}
                                        spaceId={spaceId}
                                    />
                                </div>
                            </div>

                            {spaceSettings && (spaceSettings.colors || spaceSettings.designSettings?.colors) && (
                                <div className="ai-page-creator-section">
                                    <h3><i className="fas fa-palette"></i> Color Assignment</h3>
                                    <p>Assign your space colors to different page elements. See the live preview below!</p>
                                    
                                    <div className="color-assignment-grid-new">
                                        {/* Element Assignments - Links */}
                                        <div className="color-assignments-new" style={{ marginTop: '-15px' }}>                                            
                                            {/* Background Assignment */}
                                            <div className="assignment-item-new">
                                                <div className="assignment-label">
                                                    <i className="fas fa-fill-drip"></i>
                                                    <span>Background</span>
                                                </div>
                                                <div className="color-circles">
                                                    {Object.entries(spaceSettings.colors || spaceSettings.designSettings?.colors || {}).map(([colorKey, colorValue]) => (
                                                        <div 
                                                            key={colorKey}
                                                            className={`color-circle ${colorAssignments.background === colorKey ? 'selected' : ''}`}
                                                            style={{ backgroundColor: colorValue }}
                                                            onClick={() => {
                                                                setColorAssignments(prev => ({ ...prev, background: colorKey }));
                                                                setUserModifiedColorAssignments(true);
                                                                console.log(`🎨 [USER-SELECTION] Background changed to: ${colorKey}`);
                                                            }}
                                                            title={`${colorKey}: ${colorValue}`}
                                                        ></div>
                                                    ))}
                                                </div>
                                            </div>

                                            {/* Primary Text Assignment */}
                                            <div className="assignment-item-new">
                                                <div className="assignment-label">
                                                    <i className="fas fa-heading"></i>
                                                    <span>Primary Text</span>
                                                </div>
                                                <div className="color-circles">
                                                    {Object.entries(spaceSettings.colors || spaceSettings.designSettings?.colors || {}).map(([colorKey, colorValue]) => (
                                                        <div 
                                                            key={colorKey}
                                                            className={`color-circle ${colorAssignments.primaryText === colorKey ? 'selected' : ''}`}
                                                            style={{ backgroundColor: colorValue }}
                                                            onClick={() => {
                                                                setColorAssignments(prev => ({ ...prev, primaryText: colorKey }));
                                                                setUserModifiedColorAssignments(true);
                                                                console.log(`🎨 [USER-SELECTION] Primary text changed to: ${colorKey}`);
                                                            }}
                                                            title={`${colorKey}: ${colorValue}`}
                                                        ></div>
                                                    ))}
                                                </div>
                                            </div>

                                            {/* Secondary Text Assignment */}
                                            <div className="assignment-item-new">
                                                <div className="assignment-label">
                                                    <i className="fas fa-paragraph"></i>
                                                    <span>Secondary Text</span>
                                                </div>
                                                <div className="color-circles">
                                                    {Object.entries(spaceSettings.colors || spaceSettings.designSettings?.colors || {}).map(([colorKey, colorValue]) => (
                                                        <div 
                                                            key={colorKey}
                                                            className={`color-circle ${colorAssignments.secondaryText === colorKey ? 'selected' : ''}`}
                                                            style={{ backgroundColor: colorValue }}
                                                            onClick={() => {
                                                                setColorAssignments(prev => ({ ...prev, secondaryText: colorKey }));
                                                                setUserModifiedColorAssignments(true);
                                                                console.log(`🎨 [USER-SELECTION] Secondary text changed to: ${colorKey}`);
                                                            }}
                                                            title={`${colorKey}: ${colorValue}`}
                                                        ></div>
                                                    ))}
                                                </div>
                                            </div>

                                            {/* Buttons Assignment */}
                                            <div className="assignment-item-new">
                                                <div className="assignment-label">
                                                    <i className="fas fa-mouse-pointer"></i>
                                                    <span>Buttons</span>
                                                </div>
                                                <div className="color-circles">
                                                    {Object.entries(spaceSettings.colors || spaceSettings.designSettings?.colors || {}).map(([colorKey, colorValue]) => (
                                                        <div 
                                                            key={colorKey}
                                                            className={`color-circle ${colorAssignments.buttons === colorKey ? 'selected' : ''}`}
                                                            style={{ backgroundColor: colorValue }}
                                                            onClick={() => handleButtonColorChange(colorKey)}
                                                            title={`${colorKey}: ${colorValue}`}
                                                        ></div>
                                                    ))}
                                                </div>
                                            </div>

                                            {/* Button Text Assignment */}
                                            <div className="assignment-item-new">
                                                <div className="assignment-label">
                                                    <i className="fas fa-font"></i>
                                                    <span>Button Text</span>
                                                    <small style={{ color: '#666', fontSize: '11px', marginLeft: '5px' }}>
                                                        (auto-adjusted for contrast)
                                                    </small>
                                                </div>
                                                <div className="color-circles">
                                                    {Object.entries(spaceSettings.colors || spaceSettings.designSettings?.colors || {}).map(([colorKey, colorValue]) => (
                                                        <div 
                                                            key={colorKey}
                                                            className={`color-circle ${colorAssignments.buttonText === colorKey ? 'selected' : ''}`}
                                                            style={{ backgroundColor: colorValue }}
                                                            onClick={() => {
                                                                setColorAssignments(prev => ({ ...prev, buttonText: colorKey }));
                                                                setUserModifiedColorAssignments(true);
                                                                console.log(`🎨 [USER-SELECTION] Button text changed to: ${colorKey}`);
                                                            }}
                                                            title={`${colorKey}: ${colorValue}`}
                                                        ></div>
                                                    ))}
                                                </div>
                                            </div>

                                            {/* Accents Assignment */}
                                            <div className="assignment-item-new">
                                                <div className="assignment-label">
                                                    <i className="fas fa-magic"></i>
                                                    <span>Accents</span>
                                                </div>
                                                <div className="color-circles">
                                                    {Object.entries(spaceSettings.colors || spaceSettings.designSettings?.colors || {}).map(([colorKey, colorValue]) => (
                                                        <div 
                                                            key={colorKey}
                                                            className={`color-circle ${colorAssignments.accents === colorKey ? 'selected' : ''}`}
                                                            style={{ backgroundColor: colorValue }}
                                                            onClick={() => {
                                                                setColorAssignments(prev => ({ ...prev, accents: colorKey }));
                                                                setUserModifiedColorAssignments(true);
                                                                console.log(`🎨 [USER-SELECTION] Accents changed to: ${colorKey}`);
                                                            }}
                                                            title={`${colorKey}: ${colorValue}`}
                                                        ></div>
                                                    ))}
                                                </div>
                                            </div>
                                        </div>

                                        {/* Live Preview - Rechts */}
                                        <div className="color-live-preview-new">
                                            <h4><i className="fas fa-eye"></i> Live Preview</h4>
                                            <div 
                                                className="preview-mockup"
                                                style={{ 
                                                    backgroundColor: (spaceSettings.colors || spaceSettings.designSettings?.colors)?.[colorAssignments.background],
                                                    padding: '20px',
                                                    borderRadius: '12px',
                                                    border: '1px solid #e0e0e0'
                                                }}
                                                ref={(el) => {
                                                    if (el) {
                                                        const usedColors = spaceSettings.colors || spaceSettings.designSettings?.colors;
                                                        console.log('🎨 [LIVE-PREVIEW] Rendering with colors:', {
                                                            sourceColors: usedColors,
                                                            assignments: colorAssignments,
                                                            backgroundColor: usedColors?.[colorAssignments.background],
                                                            primaryTextColor: usedColors?.[colorAssignments.primaryText],
                                                            buttonColor: usedColors?.[colorAssignments.buttons]
                                                        });
                                                    }
                                                }}
                                            >
                                                <h1 style={{ 
                                                    color: (spaceSettings.colors || spaceSettings.designSettings?.colors)?.[colorAssignments.primaryText],
                                                    margin: '0 0 10px 0',
                                                    fontSize: '24px',
                                                    fontWeight: 'bold'
                                                }}>
                                                    Your Business Title
                                                </h1>
                                                
                                                <p style={{ 
                                                    color: (spaceSettings.colors || spaceSettings.designSettings?.colors)?.[colorAssignments.secondaryText],
                                                    margin: '0 0 15px 0',
                                                    fontSize: '16px'
                                                }}>
                                                    This is how your secondary text will look on the page. Perfect for descriptions and content.
                                                </p>
                                                
                                                <button 
                                                    className="preview-button"
                                                    style={{ 
                                                        backgroundColor: (spaceSettings.colors || spaceSettings.designSettings?.colors)?.[colorAssignments.buttons],
                                                        color: (spaceSettings.colors || spaceSettings.designSettings?.colors)?.[colorAssignments.buttonText],
                                                        border: 'none',
                                                        padding: '12px 24px',
                                                        borderRadius: '8px',
                                                        fontWeight: '600',
                                                        cursor: 'pointer'
                                                    }}
                                                >
                                                    Call to Action
                                                </button>
                                                
                                                <div style={{ 
                                                    marginTop: '15px',
                                                    padding: '10px',
                                                    backgroundColor: (spaceSettings.colors || spaceSettings.designSettings?.colors)?.[colorAssignments.accents] + '20',
                                                    borderLeft: `4px solid ${(spaceSettings.colors || spaceSettings.designSettings?.colors)?.[colorAssignments.accents]}`,
                                                    borderRadius: '4px'
                                                }}>
                                                    <span style={{ 
                                                        color: (spaceSettings.colors || spaceSettings.designSettings?.colors)?.[colorAssignments.accents],
                                                        fontWeight: '600',
                                                        fontSize: '14px'
                                                    }}>
                                                        <i className="fas fa-sparkles"></i> Accent elements like highlights and special features
                                                    </span>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            )}

                            {/* Font Selection - Grid Layout wie Color Assignment */}
                            <div className="ai-page-creator-section">
                                <h3><i className="fas fa-font"></i> Typography Settings</h3>
                                <p>Choose fonts for your AI-generated pages. See the live preview on the right!</p>
                                
                                <div className="color-assignment-grid-new">
                                    {/* Font Selection - Links */}
                                    <div className="color-assignments-new">
                                        <h4><i className="fas fa-font"></i> Font Selection</h4>
                                        
                                        {/* Title Font Selection */}
                                        <div className="assignment-item-new">
                                            <div className="assignment-label">
                                                <i className="fas fa-heading"></i>
                                                <span>Title Font</span>
                                            </div>
                                            <div className="ai-font-pills">
                                                {allTitleFonts.map(font => (
                                                    <div 
                                                        key={font}
                                                        className={`ai-font-pill ${selectedFonts.globalBigFont === font ? 'selected' : ''} ${isCustomFont(font) ? 'custom-font' : ''}`}
                                                        onClick={() => setSelectedFonts(prev => ({ ...prev, globalBigFont: font }))}
                                                        style={{ fontFamily: font }}
                                                        title={isCustomFont(font) ? 'Custom uploaded font' : 'System font'}
                                                    >
                                                        <span className="ai-font-pill-text">{font}</span>
                                                        {isCustomFont(font) && (
                                                            <span className="custom-font-indicator">✨</span>
                                                        )}
                                                        {selectedFonts.globalBigFont === font && (
                                                            <i className="fas fa-check ai-font-pill-check"></i>
                                                        )}
                                                    </div>
                                                ))}
                                            </div>
                                        </div>

                                        {/* Text Font Selection */}
                                        <div className="assignment-item-new">
                                            <div className="assignment-label">
                                                <i className="fas fa-paragraph"></i>
                                                <span>Text Font</span>
                                            </div>
                                            <div className="ai-font-pills">
                                                {allTextFonts.map(font => (
                                                    <div 
                                                        key={font}
                                                        className={`ai-font-pill ${selectedFonts.globalSmallFont === font ? 'selected' : ''} ${isCustomFont(font) ? 'custom-font' : ''}`}
                                                        onClick={() => setSelectedFonts(prev => ({ ...prev, globalSmallFont: font }))}
                                                        style={{ fontFamily: font }}
                                                        title={isCustomFont(font) ? 'Custom uploaded font' : 'System font'}
                                                    >
                                                        <span className="ai-font-pill-text">{font}</span>
                                                        {isCustomFont(font) && (
                                                            <span className="custom-font-indicator">✨</span>
                                                        )}
                                                        {selectedFonts.globalSmallFont === font && (
                                                            <i className="fas fa-check ai-font-pill-check"></i>
                                                        )}
                                                    </div>
                                                ))}
                                            </div>
                                        </div>
                                    </div>

                                    {/* Font Live Preview - Rechts */}
                                    <div className="color-live-preview-new">
                                        <h4><i className="fas fa-eye"></i> Font Preview</h4>
                                        <div 
                                            className="preview-mockup"
                                            style={{ 
                                                backgroundColor: (spaceSettings.colors || spaceSettings.designSettings?.colors)?.[colorAssignments.background],
                                                padding: '20px',
                                                borderRadius: '12px',
                                                border: '1px solid #e0e0e0'
                                            }}
                                        >
                                            <h1 style={{ 
                                                fontFamily: selectedFonts.globalBigFont,
                                                margin: '0 0 15px 0',
                                                fontSize: '28px',
                                                fontWeight: '600',
                                                color: (spaceSettings.colors || spaceSettings.designSettings?.colors)?.[colorAssignments.primaryText]
                                            }}>
                                                Your Business Title
                                            </h1>
                                            
                                            <p style={{ 
                                                fontFamily: selectedFonts.globalSmallFont,
                                                margin: '0',
                                                fontSize: '16px',
                                                lineHeight: '1.5',
                                                color: (spaceSettings.colors || spaceSettings.designSettings?.colors)?.[colorAssignments.secondaryText]
                                            }}>
                                                This is how your description text will look on the page. Perfect for explaining your services and connecting with your audience.
                                            </p>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            {/* Page Count Slider */}
                            <div className="ai-page-creator-section">
                                <h3><i className="fas fa-layer-group"></i> Number of Pages</h3>
                                <p>How many pages should AI create for your business?</p>
                                
                                <div className="ai-page-count-container">
                                    <div className="ai-page-count-value">
                                        <span className="ai-page-count-number">{pageCount}</span>
                                        <span className="ai-page-count-label">Pages</span>
                                    </div>

                                    <div className="ai-page-slider-wrapper">
                                        <input
                                            type="range"
                                            min="3"
                                            max="10"
                                            value={pageCount}
                                            onChange={(e) => setPageCount(parseInt(e.target.value))}
                                            className="ai-page-count-slider"
                                        />
                                        <div className="ai-slider-track">
                                            <div 
                                                className="ai-slider-fill"
                                                style={{ width: `${((pageCount - 3) / 7) * 100}%` }}
                                            ></div>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            {error && (
                                <div className="ai-error-message">
                                    <i className="fas fa-exclamation-triangle"></i>
                                    {error}
                                </div>
                            )}
                        </>
                    )}
                </div>

                {/* Actions */}
                <div className="ai-page-creator-actions">
                    <button 
                        className="ai-popup-creation-buttons secondary"
                        onClick={() => {
                            onClose();
                            document.body.style.overflow = 'auto';
                        }}
                        disabled={creating || loading}
                    >
                        Cancel
                    </button>
                    <button 
                        className="ai-popup-creation-buttons primary"
                        onClick={handleGeneratePage}
                        disabled={!aiInputs.businessDescription || creating || loading}
                    >
                        {creating ? (
                            <>
                                <div className="btn-icon-container">
                                    <i className="fas fa-spinner fa-spin"></i>
                                </div>
                                <span className="btn-text">Creating with AI...</span>
                            </>
                        ) : (
                            <>
                                <div className="btn-icon-container">
                                    <i className="fas fa-magic"></i>
                                </div>
                                <span className="btn-text">Generate Page with AI</span>
                            </>
                        )}
                    </button>
                </div>
            </div>
        </div>
    );
};

export default AIPageCreator;

